import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Button, Radio, RadioGroup } from "@mui/material";
import { makeStyles } from "@mui/styles";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import {
  displayFriendlyDate,
  isCompoundedOrderType,
  supportedUSStatesForCompoundedMeds,
} from "~/shared/utils/sharedUtils";
import { ProductImage } from "~/visits/components/payment/paymentComponents";
import { productName } from "~/visits/components/payment/paymentHelpers";
import {
  clearForceFlavoredBBs,
  clearVisitProduct,
  getBetaBlockerVariant,
  getOriginalRefillOrderItems,
  getRefillOptions,
  getVisitID,
  getVisitUSState,
  isFollowUpVisit,
  setFollowUpVisitFlag,
  setForceFlavoredBBs,
  setRefillOptions,
} from "~/visits/utils/visitCookies";
import {
  refillDisplayOrderItemsFor,
  setProperVisitCookiesForRefills,
} from "~/visits/utils/visitUtils";
import imgFruitPunch from "~assets/images/flavors/tropical-fruit-medley-flavor.png";

import { kickBlue } from "~/shared/styles/baseStyles";

import {
  CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP,
  ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
  ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
} from "~/shared/sharedConstants";

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },
}));

export default function RefillMakeChanges({ match }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const consultationType = match.params.consultationType;
  const queryParams = new URLSearchParams(location.search);
  const checkoutSessionID = queryParams.get("checkoutSessionID");
  if (!checkoutSessionID) {
    window.location.href = "/me/portal";
  }

  const originalRefillOrderItems =
    getOriginalRefillOrderItems(checkoutSessionID);

  // Cross-visit refill options persisted by the portal's "Order a Refill"
  // click. Absent (stale pre-deploy session) or single-option sessions render
  // the legacy single-product view below.
  const refillCtx = checkoutSessionID
    ? getRefillOptions(checkoutSessionID)
    : null;
  const options = refillCtx?.options || [];
  const multiOption = options.length > 1 && !!refillCtx?.patient;
  const [selectedKey, setSelectedKey] = useState(() => {
    if (!checkoutSessionID || options.length === 0) return null;
    const stampedVisitID = getVisitID(checkoutSessionID);
    const stamped = options.find((opt) => opt.visit.id === stampedVisitID);
    return (stamped || options[0]).key;
  });
  const selectedOption = multiOption
    ? options.find((opt) => opt.key === selectedKey) || options[0]
    : null;

  // If the patient picked a different visit's product, re-stamp the checkout
  // session for that visit so the downstream flow (order items, visitID, order
  // type, BB variant) runs against the selected prescription. EVERY exit path
  // must call this first — the page's hints (dosage bullets, BB variant) are
  // rendered from the selection, so leaving the session stamped for the
  // default visit would launch a flow that contradicts what the patient saw.
  const restampSelectedOption = () => {
    if (
      multiOption &&
      selectedOption &&
      selectedOption.visit.id !== getVisitID(checkoutSessionID)
    ) {
      setProperVisitCookiesForRefills(
        refillCtx.patient,
        selectedOption.visit,
        selectedOption.prescription,
        checkoutSessionID,
        // Mid-flow stamp: keep other tabs' checkout sessions/answers alive.
        { preserveOtherSessions: true },
      );
      // Re-persist the options: the stamp call above resets the CURRENT
      // session (clearVisitCookies), and back-navigation needs them.
      setRefillOptions(refillCtx, checkoutSessionID);
    }
  };

  const handleMakeChanges = () => {
    restampSelectedOption();
    clearForceFlavoredBBs(checkoutSessionID);
    clearVisitProduct(checkoutSessionID);
    setFollowUpVisitFlag("yes", checkoutSessionID);
    history.push(
      `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`,
    );
  };

  const handleNoChanges = () => {
    restampSelectedOption();
    clearForceFlavoredBBs(checkoutSessionID);
    history.push(
      `/visits/${consultationType}/choose-refill-order-items?checkoutSessionID=${checkoutSessionID}`,
    );
  };

  const handleTryFlavoredBBs = () => {
    restampSelectedOption();
    setForceFlavoredBBs(true, checkoutSessionID);
    clearVisitProduct(checkoutSessionID);
    setFollowUpVisitFlag("yes", checkoutSessionID);
    history.push(
      `/visits/${consultationType}/1?checkoutSessionID=${checkoutSessionID}`,
    );
  };

  // this is in case user starts to make changes and then goes back to the previous page
  if (isFollowUpVisit(checkoutSessionID)) {
    window.location.href = "/me/portal";
    return null;
  }

  // Items driving the upsell/follow-up hints: the SELECTED option's items
  // (mapped to their refill equivalents for display) when the cross-visit
  // picker is active, else the stamped session's items (legacy view).
  const displayOrderItems =
    multiOption && selectedOption
      ? refillDisplayOrderItemsFor(selectedOption.visit, consultationType)
      : originalRefillOrderItems;

  // Drives the flavored-specific hint bullets (12mg → 22mg, "Try another
  // flavor") — deliberately the exact flavored refill SKUs, not the broader
  // compounded family.
  const isRefillingFlavoredBBs = displayOrderItems.some((orderItem) =>
    [
      ORDER_TYPE_PROP_1_PACK_FLAVORED_REFILL,
      ORDER_TYPE_PROP_2_PACK_FLAVORED_REFILL,
    ].includes(orderItem.productSlug),
  );

  // A compounded PRODUCT (flavored mints or Ate/Sco, incl. their *-refill
  // variants via sharedUtils' canonical list). Addon items don't count: a
  // flavored trial pack riding along on a commercial order isn't a compounded
  // prescription the patient can refill.
  const isCompoundedRefillItem = (item) =>
    !item.isAddon && isCompoundedOrderType(item.productSlug);

  const isRefillingCompounded =
    displayOrderItems.some(isCompoundedRefillItem) ||
    consultationType ===
      CONSULTATION_TYPE_ABBREVIATION_GENERALIZED_ANXIETY_FOLLOW_UP;

  const betaBlockerVariant =
    multiOption && selectedOption
      ? `${selectedOption.prescription.medicationName} ${selectedOption.prescription.medicationDosage}`
      : getBetaBlockerVariant(checkoutSessionID);

  // A compounded refill anywhere in the persisted options (not just the
  // selected one) means the patient can already refill a compounded product
  // above — upselling mints on top of that is redundant.
  const compoundedRefillAvailable = options.some((opt) =>
    refillDisplayOrderItemsFor(opt.visit, consultationType).some(
      isCompoundedRefillItem,
    ),
  );

  const showFlavoredBBUpsell =
    supportedUSStatesForCompoundedMeds.includes(getVisitUSState()) &&
    !isRefillingCompounded &&
    !compoundedRefillAvailable;

  return (
    <main
      className={classes.content}
      style={{ backgroundColor: "#eee", minHeight: "100vh" }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "0 10px",
          maxWidth: "600px",
          margin: "0 auto",
        }}
      >
        <h3 style={{ textAlign: "center", marginBottom: "20px" }}>
          Your Kick Refill
        </h3>

        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "6px",
            backgroundColor: "white",
            maxWidth: "600px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {multiOption ? (
            <>
              <h5 style={{ marginBottom: "10px" }}>
                Select the prescription to refill:
              </h5>
              <RadioGroup
                name="refill-prescription"
                aria-label="Select the prescription to refill"
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                {options.map((opt) => {
                  const selected = opt.key === selectedOption?.key;
                  const row = refillDisplayOrderItemsFor(
                    opt.visit,
                    consultationType,
                  ).find((item) => !item.isAddon) || {
                    consultationType,
                    productSlug: opt.refillOrderType,
                    subcategory: opt.subcategory,
                  };
                  const orderedAtTime = new Date(opt.orderedAt).getTime();
                  const label = productName(
                    row.productSlug,
                    row.subcategory,
                    checkoutSessionID,
                    {
                      bbName: opt.prescription.medicationName,
                      bbDosage: opt.prescription.medicationDosage,
                    },
                  );
                  return (
                    <div
                      key={opt.key}
                      onClick={() => setSelectedKey(opt.key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "4px 8px",
                        margin: "6px 0",
                        border: selected
                          ? `2px solid ${kickBlue}`
                          : "1px solid #ddd",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      <Radio
                        value={opt.key}
                        style={{ color: kickBlue }}
                        inputProps={{ "aria-label": label }}
                      />
                      <div style={{ width: "60px" }}>
                        <ProductImage
                          classes={classes}
                          consultationType={row.consultationType}
                          productType={row.productSlug}
                          subcategory={row.subcategory}
                          checkoutSessionID={checkoutSessionID}
                          imgStyle={{
                            height: "90px",
                            width: "90px",
                            objectFit: "scale-down",
                            paddingRight: "5px",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          marginLeft: "20px",
                          fontSize: "16px",
                          lineHeight: "1.5",
                          padding: "0 18px",
                        }}
                      >
                        {label}
                        <div style={{ fontSize: "13px", color: "#666" }}>
                          {orderedAtTime > 0 &&
                            `Prescribed ${displayFriendlyDate(opt.orderedAt)}`}
                          {opt.hasScopAddon && " · includes Scopolamine add-on"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </RadioGroup>
            </>
          ) : (
            <>
              <h5 style={{ marginBottom: "10px" }}>
                Reorder the following product(s):
              </h5>
              {originalRefillOrderItems.map((orderItem) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    margin: "-8px 0",
                  }}
                  key={orderItem.productSlug}
                >
                  <div style={{ width: "60px" }}>
                    <ProductImage
                      classes={classes}
                      consultationType={orderItem.consultationType}
                      productType={orderItem.productSlug}
                      subcategory={orderItem.subcategory}
                      checkoutSessionID={checkoutSessionID}
                      imgStyle={{
                        height: "90px",
                        width: "90px",
                        objectFit: "scale-down",
                        paddingRight: "5px",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      marginLeft: "20px",
                      fontSize: "16px",
                      lineHeight: "1.5",
                      padding: "0 18px",
                    }}
                  >
                    {productName(
                      orderItem.productSlug,
                      orderItem.subcategory,
                      checkoutSessionID,
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
          <p
            style={{
              margin: "10px auto 4px",
              fontSize: "16px",
              marginTop: "10px",
            }}
          >
            {/* Complete a quick follow-up visit */}
          </p>
          <Button
            color="primary"
            size="large"
            style={{
              backgroundColor: kickBlue,
              color: "white",
              margin: "10px auto 10px",
              width: "300px",
            }}
            onClick={handleNoChanges}
          >
            Continue <NavigateNextIcon />
          </Button>
        </div>

        <h4
          style={{
            textAlign: "center",
            marginBottom: "15px",
            marginTop: "24px",
            fontSize: "20px",
          }}
        >
          Need to Make Medication Changes?
        </h4>

        <div
          style={{
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "6px",
            backgroundColor: "white",
            maxWidth: "600px",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showFlavoredBBUpsell && (
            <div
              style={{
                marginBottom: "10px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <p
                style={{
                  fontSize: "20px",
                  fontFamily: "Spectral-Bold",
                  marginTop: "20px",
                  marginBottom: "16px",
                  alignSelf: "center",
                }}
              >
                - New 5x Faster & Stronger Mints -
              </p>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "24px",
                }}
              >
                <img
                  src={imgFruitPunch}
                  alt="Fruit Punch Flavored Troches"
                  style={{
                    width: "84px",
                    position: "relative",
                    marginBottom: "-10px",
                  }}
                />
                <h5 style={{ marginBottom: "0", marginLeft: "14px" }}>
                  Available in three flavors, our mints are 20% stronger and
                  start working in just 10 minutes
                </h5>
              </div>
              <Button
                variant="outlined"
                size="large"
                style={{
                  backgroundColor: kickBlue,
                  borderColor: kickBlue,
                  color: "white",
                  margin: "0 auto 10px",
                  width: "300px",
                }}
                onClick={handleTryFlavoredBBs}
              >
                Order Fast-Acting Mints <NavigateNextIcon />
              </Button>
              <hr
                style={{
                  width: "100%",
                  margin: "28px 0 0 0",
                  height: "1px",
                  backgroundColor: "#ddd",
                }}
              />
            </div>
          )}

          <h5 style={{ marginBottom: "0" }}>
            Complete a quick 5-minute follow-up to:
          </h5>
          <ul>
            <li
              style={{ paddingLeft: 0, fontSize: "16px", marginBottom: "4px" }}
            >
              Increase medication quantity
            </li>
            {["Propranolol 10mg", "Propranolol 12mg"].includes(
              betaBlockerVariant,
            ) && (
              <li
                style={{
                  paddingLeft: 0,
                  fontSize: "16px",
                  marginBottom: "4px",
                }}
              >
                {isRefillingFlavoredBBs
                  ? "Increase dosage (12mg → 22mg)"
                  : "Increase dosage (10mg → 20mg)"}
              </li>
            )}
            <li
              style={{ paddingLeft: 0, fontSize: "16px", marginBottom: "4px" }}
            >
              Pickup from local pharmacy
            </li>
            {isRefillingFlavoredBBs && (
              <li
                style={{
                  paddingLeft: 0,
                  fontSize: "16px",
                  marginBottom: "4px",
                }}
              >
                Try another flavor
              </li>
            )}
          </ul>
          <p
            style={{
              margin: "10px auto 4px",
              fontSize: "16px",
              marginTop: "10px",
            }}
          >
            {/* Complete a quick follow-up visit */}
          </p>
          <Button
            variant="outlined"
            size="large"
            style={{
              borderColor: kickBlue,
              color: kickBlue,
              margin: "0 auto 10px",
              width: "300px",
            }}
            onClick={handleMakeChanges}
          >
            Quick Follow-up Visit <NavigateNextIcon />
          </Button>
        </div>
      </div>
    </main>
  );
}
